import { useState, useEffect } from 'react';
import Web3 from 'web3';
import Web3Modal from 'web3modal';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import * as solanaWeb3 from '@solana/web3.js';
import { getOrCreateAssociatedTokenAccount, TOKEN_PROGRAM_ID, createTransferInstruction } from '@solana/spl-token';
import toast from 'react-hot-toast';

import { isEVMNetwork } from 'src/utils/blockchain';
import { EVM_CHAIN_IDS, SUPPORTED_TOKENS } from 'src/utils/constants';
import { createProviderOptions } from 'src/utils/providers';
import * as tokenAddresses from 'src/utils/tokenAddresses';
import EVM_TOKEN_ABI from 'src/utils/evmTokenABI';
import Button from 'src/components/button/Button';

const PayWithWalletBtn = ({ network, token, amount, toAddress, onComplete }) => {
    // Solana wallet
    const { connection } = useConnection();
    const {
        publicKey: solPublicKey,
        connected: solConnected,
        wallet,
        disconnect,
        sendTransaction,
        signTransaction,
    } = useWallet();
    const { setVisible } = useWalletModal();
    // UI state
    const [isWalletConnected, setIsWalletConnected] = useState();
    // payment
    const [web3Account, setWeb3Account] = useState();
    const [web3, setWeb3] = useState();

    // remove cached connections
    useEffect(() => {
        if (!isWalletConnected) {
            disconnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (solConnected) {
            setWeb3Account(solPublicKey.toBase58());
            setIsWalletConnected(true);
        }
    }, [solConnected, solPublicKey]);

    useEffect(() => {
        if (wallet) {
            wallet.adapter.connect();
        }
    }, [wallet]);

    const onConnectClick = async () => {
        if (isEVMNetwork(network)) {
            const web3Modal = new Web3Modal({
                disableInjectedProvider: false,
                cacheProvider: false, // optional
                providerOptions: createProviderOptions(EVM_CHAIN_IDS[network]), // required
            });

            const provider = await web3Modal.connect();
            const web3 = new Web3(provider);
            const accounts = await web3.eth.getAccounts();

            web3.currentProvider.on('accountsChanged', (account) => setWeb3Account(account));
            web3.currentProvider.on('chainChanged', async (currentChainId) => {
                const chainId = EVM_CHAIN_IDS[network];
                if (currentChainId !== chainId) {
                    try {
                        await web3.currentProvider.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: Web3.utils.toHex(chainId) }],
                        });
                    } catch (switchError) {
                        // This error code indicates that the chain has not been added to MetaMask.
                        if (switchError.code === 4902) {
                            toast(`The ${network} network is not in your wallet. Please add it and try again.`);
                        }
                    }
                }
            });

            setWeb3(web3);
            setWeb3Account(accounts[0]);
            setIsWalletConnected(true);
        }

        if (network === 'SOL') {
            setVisible(true);
        }
    };

    const onPayClick = async (e) => {
        e.preventDefault();
        if (isEVMNetwork(network) && web3 && web3Account) {
            // switch to the right network
            const currentChainId = await web3.eth.getChainId();
            if (EVM_CHAIN_IDS[network] !== currentChainId) {
                toast.error(`You are connected to the wrong network.\nPlease switch to the ${network} network`);
                try {
                    await web3.currentProvider.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: Web3.utils.toHex(EVM_CHAIN_IDS[network]) }],
                    });
                } catch (err) {}
            }
            if (EVM_CHAIN_IDS[network] !== currentChainId) {
                return;
            }
            if (token === SUPPORTED_TOKENS[network][0]) {
                web3.eth
                    .sendTransaction({
                        from: web3Account,
                        to: toAddress,
                        value: Web3.utils.toHex(Web3.utils.toWei(amount.toString(), 'ether')),
                    })
                    .on('transactionHash', () => {
                        displaySuccessToastAndComplete();
                    })
                    .on('error', () => {
                        displayError();
                    });
            } else {
                const tokenAddress = tokenAddresses[`${network.toLowerCase()}TokenAddresses`][token];
                if (!tokenAddress) return;
                const contract = new web3.eth.Contract(EVM_TOKEN_ABI, tokenAddress);
                web3.eth
                    .sendTransaction({
                        from: web3Account,
                        to: tokenAddress,
                        data: contract.methods
                            .transfer(toAddress, Web3.utils.toWei(amount.toString(), 'ether'))
                            .encodeABI(),
                    })
                    .on('transactionHash', () => {
                        displaySuccessToastAndComplete();
                    })
                    .on('error', () => {
                        displayError();
                    });
            }
        }

        if (network === 'SOL' && solPublicKey && connection) {
            if (token === 'SOL') {
                try {
                    const transaction = new solanaWeb3.Transaction().add(
                        solanaWeb3.SystemProgram.transfer({
                            fromPubkey: solPublicKey,
                            toPubkey: new solanaWeb3.PublicKey(toAddress),
                            lamports: solanaWeb3.LAMPORTS_PER_SOL * amount,
                        })
                    );
                    const txId = await sendTransaction(transaction, connection);
                    const latestBlockHash = await connection.getLatestBlockhash();
                    await connection.confirmTransaction({
                        signature: txId,
                        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
                        blockhash: latestBlockHash.blockhash,
                    });
                    displaySuccessToastAndComplete();
                } catch (error) {
                    displayError();
                }
            } else {
                try {
                    const tokenAddress = tokenAddresses.SOL_TOKEN_ADDRESSES[token];
                    if (!tokenAddress) return;

                    const tokenPubKey = new solanaWeb3.PublicKey(tokenAddress);
                    const fromTokenAccount = await getOrCreateAssociatedTokenAccount(
                        connection,
                        solPublicKey,
                        tokenPubKey,
                        solPublicKey,
                        signTransaction
                    );

                    const toTokenAccount = await getOrCreateAssociatedTokenAccount(
                        connection,
                        solPublicKey,
                        tokenPubKey,
                        new solanaWeb3.PublicKey(toAddress),
                        signTransaction
                    );

                    const transaction = new solanaWeb3.Transaction().add(
                        createTransferInstruction(
                            fromTokenAccount.address, // source
                            toTokenAccount.address, // dest
                            solPublicKey,
                            parseInt(amount * Math.pow(10, 6)),
                            [],
                            TOKEN_PROGRAM_ID
                        )
                    );

                    const latestBlockHash = await connection.getLatestBlockhash();
                    transaction.recentBlockhash = latestBlockHash.blockhash;
                    transaction.feePayer = solPublicKey;
                    const signed = await signTransaction(transaction);
                    const signature = await connection.sendRawTransaction(signed.serialize());
                    await connection.confirmTransaction({
                        signature,
                        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
                        blockhash: latestBlockHash.blockhash,
                    });
                    displaySuccessToastAndComplete();
                } catch (error) {
                    displayError();
                }
            }
        }
    };

    const displayError = () => {
        toast.error('Could not send the transaction... Please try again');
    };

    const displaySuccessToastAndComplete = () => {
        toast.success(
            'Thank you for the deposit. Your transaction has been sent successfully and will appear in your account soon!'
        );
        onComplete();
    };

    return (
        <div className="w-full">
            {isWalletConnected ? (
                <Button text={`Pay ${amount} ${token}`} onClick={onPayClick} primary className="w-full" />
            ) : (
                <Button text="Connect Wallet" onClick={onConnectClick} primary className="w-full" />
            )}
        </div>
    );
};

export default PayWithWalletBtn;
